import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardList,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Upload,
  X,
  ImagePlus,
  Truck,
  PackageCheck,
  Calculator,
  FileText,
  Download,
} from 'lucide-react';
import { profileApi, orderApi, uploadApi } from '@/user/api';
import type { OrderVO, OrderFeeSettleBatchVO } from '@/types/api-entity';

export default function OrdersPage() {
  const [activeTab, setActiveTab] = useState<'buyer' | 'supplier'>('buyer');
  const [orders, setOrders] = useState<OrderVO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // 批量结算选中
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);

  // 结算批次映射（按订单ID）
  const [settleBatchesMap, setSettleBatchesMap] = useState<Record<number, OrderFeeSettleBatchVO[]>>({});

  // 凭证折叠状态
  const [expandedProofs, setExpandedProofs] = useState<Record<number, { ship?: boolean; receive?: boolean; settle?: boolean }>>({});

  // 发货弹窗状态
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [shipModalOrderId, setShipModalOrderId] = useState<number | null>(null);
  const [shipFiles, setShipFiles] = useState<File[]>([]);
  const [shipPreviews, setShipPreviews] = useState<string[]>([]);
  const [shipLoading, setShipLoading] = useState(false);
  const shipInputRef = useRef<HTMLInputElement>(null);

  // 收货弹窗状态
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveModalOrderId, setReceiveModalOrderId] = useState<number | null>(null);
  const [receiveFiles, setReceiveFiles] = useState<File[]>([]);
  const [receivePreviews, setReceivePreviews] = useState<string[]>([]);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const receiveInputRef = useRef<HTMLInputElement>(null);

  // 批量结算弹窗状态
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [settleFiles, setSettleFiles] = useState<File[]>([]);
  const [settlePreviews, setSettlePreviews] = useState<string[]>([]);
  const [settleRemark, setSettleRemark] = useState('');
  const [settleLoading, setSettleLoading] = useState(false);
  const settleInputRef = useRef<HTMLInputElement>(null);

  // 线下合同接单弹窗状态
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptOrderId, setAcceptOrderId] = useState<number | null>(null);
  const [acceptAmount, setAcceptAmount] = useState('');
  const [acceptFiles, setAcceptFiles] = useState<File[]>([]);
  const [acceptPreviews, setAcceptPreviews] = useState<string[]>([]);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const acceptInputRef = useRef<HTMLInputElement>(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelectedOrderIds([]);
    try {
      const res =
        activeTab === 'buyer'
          ? await profileApi.getMyOrders()
          : await profileApi.getMySupplierOrders();
      if (res.code === 200 && res.data) {
        let list = res.data;
        // 供应商只显示平台审核通过的订单（status >= 2 且非取消/拒绝）
        if (activeTab === 'supplier') {
          list = list.filter((o) => o.status !== 1 && o.status !== 6 && o.status !== 7);
        }
        setOrders(list);
        // 加载每个订单的结算批次
        list.forEach((o) => {
          if (o.status !== undefined && o.status >= 3) {
            loadSettleBatches(o.id);
          }
        });
      } else {
        setError(res.message || '加载失败');
      }
    } catch (err: any) {
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const loadSettleBatches = async (orderId: number) => {
    try {
      const res = await orderApi.getFeePayments(orderId);
      if (res.code === 200 && res.data) {
        setSettleBatchesMap((prev) => ({ ...prev, [orderId]: res.data }));
      }
    } catch (err) {
      console.error('加载结算批次失败:', err);
    }
  };

  const handleAcceptOrder = async (id: number) => {
    let order = await orderApi.getOrderDetail(id);
    let supplier : any = order.data
    //线下合同需要上传需要上传合同金额和合同文件
    if(supplier?.supplierFeeConfig?.feeBaseType === 1){
      setAcceptOrderId(id);
      setAcceptAmount('');
      setAcceptFiles([]);
      setAcceptPreviews([]);
      setAcceptModalOpen(true);
    }else{
      handleSettleOrder(id)
    }
  };
  const handleSettleOrder = async (id: number) => {
    if (!confirm('确认接单？')) return;
    setActionLoading(id);
    setError('');
    try {
      const res = await orderApi.acceptOrder(id);
      if (res.code === 200) {
        loadOrders();
      } else {
        setError(res.message || '接单失败');
      }
    } catch (err: any) {
      setError(err.message || '接单失败');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectOrder = async (id: number) => {
    if (!confirm('确认拒绝接单？拒绝后订单将关闭。')) return;
    setActionLoading(id);
    setError('');
    try {
      const res = await orderApi.rejectOrder(id);
      if (res.code === 200) {
        loadOrders();
      } else {
        setError(res.message || '拒单失败');
      }
    } catch (err: any) {
      setError(err.message || '拒单失败');
    } finally {
      setActionLoading(null);
    }
  };

  const closeAcceptModal = () => {
    setAcceptModalOpen(false);
    setAcceptOrderId(null);
    setAcceptAmount('');
    setAcceptFiles([]);
    setAcceptPreviews([]);
  };

  const handleAcceptFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    console.log(files,'files')
    if (files.length === 0) return;
    const file = files[0];
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      alert(`${file.name} 不是PDF文件`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert(`${file.name} 超过10MB`);
      return;
    }
    setAcceptFiles([file]);
    setAcceptPreviews(['']);
    e.target.value = '';
  };

  const removeAcceptFile = () => {
    if (acceptPreviews[0]) {
      URL.revokeObjectURL(acceptPreviews[0]);
    }
    setAcceptFiles([]);
    setAcceptPreviews([]);
  };

  const handleAcceptSubmit = async () => {
    if (!acceptAmount) {
      alert('请输入合作金额');
      return;
    }
    if (acceptFiles.length === 0) {
      alert('请上传合作文件');
      return;
    }
    setAcceptLoading(true);
    try {
      const url = await uploadApi.uploadFile(acceptFiles[0]);
      console.log(url,'url',acceptFiles)
      if (!url) {
        alert('文件上传失败');
        return;
      }
      const res = await orderApi.acceptOrder(acceptOrderId!, {
        contractAmount: acceptAmount,
        contractFile: JSON.stringify(url),
      });
      if (res.code === 200) {
        loadOrders();
        closeAcceptModal();
      } else {
        alert(res.message || '接单失败');
      }
    } catch (err: any) {
      alert(err.message || '接单失败');
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleDownloadFile = (contractFile: string) => {
    try {
      const fileInfo = JSON.parse(contractFile);
      let url = fileInfo.url;
      
      if (!url.startsWith('http')) {
        url = `${import.meta.env.VITE_API_BASE_URL}${url}`;
      }
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileInfo.originalName || 'download';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || '下载失败');
    }
  };

  // ===== 发货相关 =====
  const openShipModal = (orderId: number) => {
    setShipModalOrderId(orderId);
    setShipModalOpen(true);
    setShipFiles([]);
    setShipPreviews([]);
  };

  const closeShipModal = () => {
    setShipModalOpen(false);
    setShipModalOrderId(null);
    setShipFiles([]);
    setShipPreviews([]);
  };

  const handleShipFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const total = shipFiles.length + files.length;
    if (total > 4) {
      alert('最多上传4张图片');
      return;
    }
    const validFiles = files.filter((f) => {
      if (!f.type.startsWith('image/')) {
        alert(`${f.name} 不是图片文件`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        alert(`${f.name} 超过10MB`);
        return false;
      }
      return true;
    });

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setShipPreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
    setShipFiles((prev) => [...prev, ...validFiles]);
  };

  const removeShipFile = (index: number) => {
    setShipFiles((prev) => prev.filter((_, i) => i !== index));
    setShipPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleShipSubmit = async () => {
    if (!shipModalOrderId) return;
    if (shipFiles.length === 0) {
      alert('请至少上传1张发货凭证图片');
      return;
    }
    setShipLoading(true);
    setError('');
    try {
      const urls = await Promise.all(shipFiles.map((f) => uploadApi.uploadImage(f)));
      const validUrls = urls.filter((u): u is string => !!u);
      if (validUrls.length !== shipFiles.length) {
        throw new Error('部分图片上传失败，请重试');
      }
      const res = await orderApi.shipOrder({ orderId: shipModalOrderId, shipProof: validUrls });
      if (res.code === 200) {
        alert('发货成功');
        closeShipModal();
        loadOrders();
      } else {
        setError(res.message || '发货失败');
      }
    } catch (err: any) {
      setError(err.message || '发货失败');
    } finally {
      setShipLoading(false);
    }
  };

  // ===== 收货相关 =====
  const openReceiveModal = (orderId: number) => {
    setReceiveModalOrderId(orderId);
    setReceiveModalOpen(true);
    setReceiveFiles([]);
    setReceivePreviews([]);
  };

  const closeReceiveModal = () => {
    setReceiveModalOpen(false);
    setReceiveModalOrderId(null);
    setReceiveFiles([]);
    setReceivePreviews([]);
  };

  const handleReceiveFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const total = receiveFiles.length + files.length;
    if (total > 4) {
      alert('最多上传4张图片');
      return;
    }
    const validFiles = files.filter((f) => {
      if (!f.type.startsWith('image/')) {
        alert(`${f.name} 不是图片文件`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        alert(`${f.name} 超过10MB`);
        return false;
      }
      return true;
    });

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setReceivePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
    setReceiveFiles((prev) => [...prev, ...validFiles]);
  };

  const removeReceiveFile = (index: number) => {
    setReceiveFiles((prev) => prev.filter((_, i) => i !== index));
    setReceivePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReceiveSubmit = async () => {
    if (!receiveModalOrderId) return;
    if (receiveFiles.length === 0) {
      alert('请至少上传1张收货凭证图片');
      return;
    }
    setReceiveLoading(true);
    setError('');
    try {
      const urls = await Promise.all(receiveFiles.map((f) => uploadApi.uploadImage(f)));
      const validUrls = urls.filter((u): u is string => !!u);
      if (validUrls.length !== receiveFiles.length) {
        throw new Error('部分图片上传失败，请重试');
      }
      const res = await orderApi.confirmReceive({ orderId: receiveModalOrderId, receiveProof: validUrls });
      if (res.code === 200) {
        alert('确认收货成功');
        closeReceiveModal();
        loadOrders();
      } else {
        setError(res.message || '确认收货失败');
      }
    } catch (err: any) {
      setError(err.message || '确认收货失败');
    } finally {
      setReceiveLoading(false);
    }
  };

  // ===== 批量结算相关 =====
  const isOrderSettleSelectable = (order: OrderVO) => {
    if (order.status !== 5) return false;
    if (activeTab === 'supplier') {
      return order.supplierFeeSettleStatus === 0 && (order.supplierFee || 0) > 0;
    }
    return order.buyerFeeSettleStatus === 0 && (order.buyerFee || 0) > 0;
  };

  const getOrderFee = (order: OrderVO) => {
    return activeTab === 'supplier' ? (order.supplierFee || 0) : (order.buyerFee || 0);
  };

  const toggleSelectOrder = (orderId: number) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleProof = (orderId: number, type: 'ship' | 'receive' | 'settle') => {
    setExpandedProofs((prev) => ({
      ...prev,
      [orderId]: { ...prev[orderId], [type]: !prev[orderId]?.[type] },
    }));
  };

  const selectedOrders = orders.filter((o) => selectedOrderIds.includes(o.id));
  const selectedTotalFee = selectedOrders.reduce((sum, o) => sum + getOrderFee(o), 0);

  const openSettleModal = () => {
    if (selectedOrderIds.length === 0) {
      alert('请至少选择1个订单');
      return;
    }
    setSettleModalOpen(true);
    setSettleFiles([]);
    setSettlePreviews([]);
    setSettleRemark('');
  };

  const closeSettleModal = () => {
    setSettleModalOpen(false);
    setSettleFiles([]);
    setSettlePreviews([]);
    setSettleRemark('');
  };

  const handleSettleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const total = settleFiles.length + files.length;
    if (total > 4) {
      alert('最多上传4张图片');
      return;
    }
    const validFiles = files.filter((f) => {
      if (!f.type.startsWith('image/')) {
        alert(`${f.name} 不是图片文件`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        alert(`${f.name} 超过10MB`);
        return false;
      }
      return true;
    });

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setSettlePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
    setSettleFiles((prev) => [...prev, ...validFiles]);
  };

  const removeSettleFile = (index: number) => {
    setSettleFiles((prev) => prev.filter((_, i) => i !== index));
    setSettlePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSettleSubmit = async () => {
    if (selectedOrderIds.length === 0) return;
    if (settleFiles.length === 0) {
      alert('请至少上传1张服务费支付凭证图片');
      return;
    }
    setSettleLoading(true);
    setError('');
    try {
      const urls = await Promise.all(settleFiles.map((f) => uploadApi.uploadImage(f)));
      const validUrls = urls.filter((u): u is string => !!u);
      if (validUrls.length !== settleFiles.length) {
        throw new Error('部分图片上传失败，请重试');
      }
      const data = {
        orderIds: selectedOrderIds,
        paymentProof: validUrls,
        payerRemark: settleRemark || undefined,
      };
      const res =
        activeTab === 'supplier'
          ? await orderApi.batchSettleFeeSupplier(data)
          : await orderApi.batchSettleFeeBuyer(data);
      if (res.code === 200) {
        alert('批量结算提交成功，等待平台审核');
        closeSettleModal();
        setSelectedOrderIds([]);
        loadOrders();
      } else {
        setError(res.message || '结算提交失败');
      }
    } catch (err: any) {
      setError(err.message || '结算提交失败');
    } finally {
      setSettleLoading(false);
    }
  };

  // 凭证状态文案
  const getFeePaymentStatusText = (status?: number) => {
    switch (status) {
      case 0:
        return '待审核';
      case 1:
        return '审核通过';
      case 2:
        return '审核拒绝';
      default:
        return '未知';
    }
  };

  const getFeePaymentStatusClass = (status?: number) => {
    switch (status) {
      case 0:
        return 'text-amber-600 bg-amber-50';
      case 1:
        return 'text-green-600 bg-green-50';
      case 2:
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatPrice = (price?: number) => {
    if (price === undefined) return '¥0';
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getStatusBadge = (status?: number) => {
    switch (status) {
      case 1:
        return <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-full">待平台审核</span>;
      case 2:
        return <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full">平台审核通过</span>;
      case 3:
        return <span className="px-2 py-0.5 bg-green-50 text-green-600 text-xs rounded-full">供应商已接单</span>;
      case 4:
        return <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full">已发货</span>;
      case 5:
        return <span className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded-full">已完成</span>;
      case 6:
        return <span className="px-2 py-0.5 bg-gray-50 text-gray-500 text-xs rounded-full">已取消</span>;
      case 7:
        return <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full">平台审核拒绝</span>;
      case 8:
        return <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full">供应商拒单</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded-full">未知</span>;
    }
  };
  const getFirstImage = (mainImage: string | string[] | undefined): string => {
    if (!mainImage) return 'https://via.placeholder.com/40';
    
    let firstUrl = '';
    if (Array.isArray(mainImage)) {
      firstUrl = mainImage[0] || '';
    } else if (typeof mainImage === 'string') {
      // 假设是多张图片逗号分隔的情况
      firstUrl = mainImage.split(',')[0].trim();
    }
    
    return firstUrl || 'https://via.placeholder.com/40';
  };

  // 渲染多图上传区域
  const renderMultiImageUploader = (
    files: string[],
    onAdd: () => void,
    onRemove: (i: number) => void,
    inputRef: React.RefObject<HTMLInputElement>,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    label: string,
    hint: string
  ) => (
    <div>
      <p className="text-sm text-gray-500 mb-2">{label}（{files.length}/4）</p>
      <input type="file" accept="image/*" multiple ref={inputRef} onChange={onChange} className="hidden" />
      <div className="flex flex-wrap gap-3">
        {files.map((preview, idx) => (
          <div key={idx} className="relative w-20 h-20">
            <img src={preview} alt={`preview-${idx}`} className="w-full h-full rounded-lg object-cover border border-gray-200" />
            <button
              onClick={() => onRemove(idx)}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {files.length < 4 && (
          <button
            onClick={onAdd}
            className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-[#1c5bff] hover:bg-blue-50/50 transition-colors"
          >
            <ImagePlus className="w-6 h-6 text-gray-400" />
            <span className="text-[10px] text-gray-400">{hint}</span>
          </button>
        )}
      </div>
    </div>
  );
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <ClipboardList className="w-6 h-6 text-[#1c5bff]" />
        订单管理
      </h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('buyer')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'buyer'
              ? 'bg-[#1c5bff] text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          我的采购订单
        </button>
        <button
          onClick={() => setActiveTab('supplier')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'supplier'
              ? 'bg-[#1c5bff] text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          我的销售订单
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#1c5bff] animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-16 text-center">
          <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {activeTab === 'buyer' ? '暂无采购订单' : '暂无销售订单'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const exp = expandedProofs[order.id] || {};
            const myFee = activeTab === 'supplier' ? order.supplierFee : order.buyerFee;
            const mySettleStatus = activeTab === 'supplier' ? order.supplierFeeSettleStatus : order.buyerFeeSettleStatus;
            return (
              <div key={order.id} className="bg-white rounded-xl shadow-sm p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {isOrderSettleSelectable(order) && (
                      <input
                        type="checkbox"
                        checked={selectedOrderIds.includes(order.id)}
                        onChange={() => toggleSelectOrder(order.id)}
                        className="w-4 h-4 text-[#1c5bff] rounded border-gray-300 focus:ring-[#1c5bff] cursor-pointer"
                      />
                    )}
                    {!isOrderSettleSelectable(order) && order.status === 5 && (
                      <span className="w-4" />
                    )}
                    <span className="text-sm font-medium text-gray-900">{order.orderNo}</span>
                    {getStatusBadge(order.status)}
                    {/* 服务费结算状态 */}
                    {order.status === 5 && mySettleStatus !== undefined && mySettleStatus > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getFeePaymentStatusClass(mySettleStatus)}`}>
                        服务费{getFeePaymentStatusText(mySettleStatus)}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">{order.createTime}</span>
                </div>

                {/* Items —— 紧凑显示 */}
                <div className="space-y-2 mb-3">
                  {order.items?.slice(0, 3).map((item) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <img
                        src={getFirstImage(item.productImage)}
                        alt={item.productName}
                        className="w-10 h-10 rounded object-cover bg-gray-100"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{item.productName}</p>
                        <p className="text-xs text-gray-400">
                          {formatPrice(item.price)} × {item.quantity}
                        </p>
                      </div>
                      <span className="text-sm text-[#1c5bff]">
                        {formatPrice(item.subtotal)}
                      </span>
                    </div>
                  ))}
                  {order.items && order.items.length > 3 && (
                    <p className="text-xs text-gray-400 pl-13">等 {order.items.length} 件商品</p>
                  )}
                </div>

                {/* Summary —— 单行紧凑 */}
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-gray-500">
                      总额 
                      <span className="text-gray-900 font-medium">
                        {formatPrice(order.totalAmount)}
                      </span>
                    </span>
                    <span className="text-gray-500">
                      服务费 <span className="text-amber-700 font-medium">{formatPrice(order.platformFee)}</span>
                    </span>
                    {activeTab === 'buyer' && (
                      <span className="text-gray-500">
                        实付 <span className="text-[#1c5bff] font-medium">{formatPrice(order.buyerPayAmount)}</span>
                      </span>
                    )}
                    {activeTab === 'supplier' && (
                      <span className="text-gray-500">
                        实收 <span className="text-green-600 font-medium">{formatPrice(order.supplierReceiveAmount)}</span>
                      </span>
                    )}
                    <span className="text-gray-500">
                      {activeTab === 'buyer' ? '供应商' : '采购商'}
                      <span className="text-gray-900 ml-1">{activeTab === 'buyer' ? order.supplierCompanyName || '-' : order.buyerCompanyName || '-'}</span>
                    </span>
                  </div>

                  {/* 收货信息 */}
                  {(activeTab === 'buyer' || (activeTab === 'supplier' && order.status !== undefined && order.status >= 3)) && order.receiveAddress && (
                    <div className="mt-2 text-xs text-gray-500 truncate">
                      {order.receiveAddress} · {order.receiveContact} · {order.receivePhone}
                    </div>
                  )}
                  {/* 线下合同 金额 + 文件 */}
                  {order.supplierFeeConfig && order.supplierFeeConfig.feeBaseType === 1 && order.contractAmount  && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <div className="mt-2 text-xs text-gray-500">
                        合同金额 <span className="text-gray-900 font-medium">{formatPrice(order.contractAmount)}</span>
                      </div>
                      <div className="mt-2 text-xs text-gray-500 flex items-center">
                        合同文件: 
                        <button
                          onClick={() => handleDownloadFile(order.contractFile)}
                          className="text-[#1c5bff] font-medium hover:underline ml-1 flex items-center gap-1"
                        >
                          {JSON.parse(order.contractFile).originalName}
                          <Download className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 平台审核拒绝原因 */}
                  {order.status === 7 && order.platformAuditRemark && (
                    <div className="mt-2 text-xs flex items-start gap-1.5 text-red-500">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>审核不通过：{order.platformAuditRemark}</span>
                    </div>
                  )}

                  {/* 发货凭证 —— 可折叠 */}
                  {order.shipProof && order.shipProof.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-50">
                      <button
                        onClick={() => toggleProof(order.id, 'ship')}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                      >
                        <Truck className="w-3 h-3" />
                        发货凭证（{order.shipProof.length}张）
                        <span className="text-[10px] text-gray-400 ml-1">{exp.ship ? '收起' : '展开'}</span>
                      </button>
                      {exp.ship && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {order.shipProof.map((url, idx) => (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt="发货凭证" className="w-16 h-16 rounded object-cover border border-gray-200 hover:border-[#1c5bff] transition-colors cursor-pointer" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 收货凭证 —— 可折叠 */}
                  {order.receiveProof && order.receiveProof.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-50">
                      <button
                        onClick={() => toggleProof(order.id, 'receive')}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                      >
                        <PackageCheck className="w-3 h-3" />
                        收货凭证（{order.receiveProof.length}张）
                        <span className="text-[10px] text-gray-400 ml-1">{exp.receive ? '收起' : '展开'}</span>
                      </button>
                      {exp.receive && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {order.receiveProof.map((url, idx) => (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt="收货凭证" className="w-16 h-16 rounded object-cover border border-gray-200 hover:border-[#1c5bff] transition-colors cursor-pointer" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 结算批次 —— 可折叠 */}
                  {settleBatchesMap[order.id] && settleBatchesMap[order.id].length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-50">
                      <button
                        onClick={() => toggleProof(order.id, 'settle')}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                      >
                        <Calculator className="w-3 h-3" />
                        服务费结算记录（{settleBatchesMap[order.id].length}条）
                        <span className="text-[10px] text-gray-400 ml-1">{exp.settle ? '收起' : '展开'}</span>
                      </button>
                      {exp.settle && (
                        <div className="mt-2 space-y-2">
                          {settleBatchesMap[order.id].map((batch) => (
                            <div key={batch.id} className="bg-gray-50 rounded-lg p-2.5">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-gray-500">{batch.settleNo || batch.id}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getFeePaymentStatusClass(batch.status)}`}>
                                  {getFeePaymentStatusText(batch.status)}
                                </span>
                              </div>
                              <div className="text-xs text-gray-700">
                                结算金额：{formatPrice(batch.amount)}
                              </div>
                              {batch.paymentProof && batch.paymentProof.length > 0 && (
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  {batch.paymentProof.map((url, idx) => (
                                    <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                      <img src={url} alt="支付凭证" className="w-12 h-12 rounded object-cover border border-gray-200 hover:border-[#1c5bff] transition-colors" />
                                    </a>
                                  ))}
                                </div>
                              )}
                              {batch.auditRemark && (
                                <div className={`text-[10px] mt-1 ${batch.status === 2 ? 'text-red-500' : 'text-gray-500'}`}>
                                  审核备注：{batch.auditRemark}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Supplier actions：接单/拒单 */}
                  {activeTab === 'supplier' && order.status === 2 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleRejectOrder(order.id)}
                        disabled={actionLoading === order.id}
                        className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs flex items-center gap-1 disabled:opacity-50"
                      >
                        {actionLoading === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                        拒绝接单
                      </button>
                      <button
                        onClick={() => handleAcceptOrder(order.id)}
                        disabled={actionLoading === order.id}
                        className="px-3 py-1.5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors text-xs flex items-center gap-1 disabled:opacity-50"
                      >
                        {actionLoading === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                        确认接单
                      </button>
                    </div>
                  )}

                  {/* Supplier actions：确认发货 */}
                  {activeTab === 'supplier' && order.status === 3 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                      <span className="text-xs text-purple-600 flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        请上传发货凭证后发货
                      </span>
                      <button
                        onClick={() => openShipModal(order.id)}
                        disabled={actionLoading === order.id}
                        className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs flex items-center gap-1 disabled:opacity-50"
                      >
                        {actionLoading === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />}
                        发货
                      </button>
                    </div>
                  )}

                  {/* Buyer actions：确认收货 */}
                  {activeTab === 'buyer' && order.status === 4 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                      <span className="text-xs text-purple-600 flex items-center gap-1">
                        <PackageCheck className="w-3 h-3" />
                        已发货，请上传收货凭证
                      </span>
                      <button
                        onClick={() => openReceiveModal(order.id)}
                        disabled={actionLoading === order.id}
                        className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs flex items-center gap-1 disabled:opacity-50"
                      >
                        {actionLoading === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageCheck className="w-3 h-3" />}
                        收货
                      </button>
                    </div>
                  )}

                  {/* 已完成状态 + 未结算提示 */}
                  {order.status === 5 && mySettleStatus === 0 && (myFee || 0) > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        服务费 {formatPrice(myFee)} 未结算
                      </span>
                      {!selectedOrderIds.includes(order.id) && (
                        <span className="text-[10px] text-gray-400">可勾选批量结算</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 批量结算底部栏 */}
      {selectedOrderIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white rounded-xl shadow-lg border border-gray-200 px-6 py-4 flex items-center gap-6">
          <div className="flex items-center gap-2 text-sm">
            <Calculator className="w-4 h-4 text-[#1c5bff]" />
            <span className="text-gray-600">
              已选 <span className="font-medium text-gray-900">{selectedOrderIds.length}</span> 个订单
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">
              合计服务费：<span className="font-medium text-red-600">{formatPrice(selectedTotalFee)}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedOrderIds([])}
              className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              清空
            </button>
            <button
              onClick={openSettleModal}
              className="px-4 py-1.5 bg-[#1c5bff] text-white text-sm rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-1.5"
            >
              <Upload className="w-3.5 h-3.5" />
              批量结算
            </button>
          </div>
        </div>
      )}

      {/* 发货弹窗 */}
      {shipModalOpen && shipModalOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Truck className="w-5 h-5 text-purple-600" />
                确认发货
              </h3>
              <button onClick={closeShipModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-500">请上传1~4张发货凭证图片，确认后将通知采购商。</p>
              {renderMultiImageUploader(
                shipPreviews,
                () => shipInputRef.current?.click(),
                removeShipFile,
                shipInputRef,
                handleShipFileChange,
                '发货凭证',
                '上传'
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100">
              <button onClick={closeShipModal} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                取消
              </button>
              <button
                onClick={handleShipSubmit}
                disabled={shipLoading || shipFiles.length === 0}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {shipLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                {shipLoading ? '提交中...' : '确认发货'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 收货弹窗 */}
      {receiveModalOpen && receiveModalOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <PackageCheck className="w-5 h-5 text-purple-600" />
                确认收货
              </h3>
              <button onClick={closeReceiveModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-500">请上传1~4张收货凭证图片，确认后订单将标记为已完成。</p>
              {renderMultiImageUploader(
                receivePreviews,
                () => receiveInputRef.current?.click(),
                removeReceiveFile,
                receiveInputRef,
                handleReceiveFileChange,
                '收货凭证',
                '上传'
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100">
              <button onClick={closeReceiveModal} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                取消
              </button>
              <button
                onClick={handleReceiveSubmit}
                disabled={receiveLoading || receiveFiles.length === 0}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {receiveLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />}
                {receiveLoading ? '提交中...' : '确认收货'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批量结算弹窗 */}
      {settleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#1c5bff]" />
                批量结算服务费
              </h3>
              <button onClick={closeSettleModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* 已选订单 */}
              <div>
                <p className="text-sm text-gray-500 mb-2">已选订单（共 {selectedOrders.length} 个）</p>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {selectedOrders.map((o) => (
                    <div key={o.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                      <span className="text-gray-700">{o.orderNo}</span>
                      <span className="text-red-600 font-medium">{formatPrice(getOrderFee(o))}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-right text-sm">
                  合计服务费：<span className="font-bold text-red-600">{formatPrice(selectedTotalFee)}</span>
                </div>
              </div>

              {/* 凭证上传 */}
              {renderMultiImageUploader(
                settlePreviews,
                () => settleInputRef.current?.click(),
                removeSettleFile,
                settleInputRef,
                handleSettleFileChange,
                '服务费支付凭证',
                '上传'
              )}

              {/* 备注 */}
              <div>
                <label className="block text-sm text-gray-500 mb-1">付款备注（选填）</label>
                <textarea
                  value={settleRemark}
                  onChange={(e) => setSettleRemark(e.target.value)}
                  placeholder="可填写付款时间、付款方式等信息"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1c5bff] focus:ring-1 focus:ring-[#1c5bff] resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100">
              <button onClick={closeSettleModal} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                取消
              </button>
              <button
                onClick={handleSettleSubmit}
                disabled={settleLoading || settleFiles.length === 0}
                className="px-4 py-2 bg-[#1c5bff] text-white text-sm rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {settleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {settleLoading ? '提交中...' : '提交结算'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 线下合同接单弹窗 */}
      {acceptModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#1c5bff]" />
                线下合同接单
              </h3>
              <button onClick={closeAcceptModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">合作金额</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">¥</span>
                  <input
                    type="number"
                    value={acceptAmount}
                    onChange={(e) => setAcceptAmount(e.target.value)}
                    placeholder="请输入合作金额"
                    className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1c5bff] focus:ring-1 focus:ring-[#1c5bff]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-1">合作文件</label>
                <input
                  ref={acceptInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleAcceptFileChange}
                  className="hidden"
                />
                {acceptFiles.length === 0 ? (
                  <button
                    onClick={() => acceptInputRef.current?.click()}
                    className="w-full h-24 border-2 border-dashed border-gray-200 rounded-lg hover:border-[#1c5bff] hover:bg-[#1c5bff]/5 transition-colors flex flex-col items-center justify-center gap-2"
                  >
                    <Upload className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-400">点击上传合作文件</span>
                    <span className="text-xs text-gray-400">只支持PDF格式，单个不超过10MB</span>
                  </button>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      {acceptPreviews[0] ? (
                        <img src={acceptPreviews[0]} alt="" className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-gray-500" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-700 truncate max-w-[200px]">{acceptFiles[0].name}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeAcceptFile()}
                      className="px-3 py-1 text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      替换
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100">
              <button onClick={closeAcceptModal} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                取消
              </button>
              <button
                onClick={handleAcceptSubmit}
                disabled={acceptLoading || !acceptAmount || acceptFiles.length === 0}
                className="px-4 py-2 bg-[#1c5bff] text-white text-sm rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {acceptLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />}
                {acceptLoading ? '提交中...' : '确认接单'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
